import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { useStore } from 'src/stores';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { announceSetupChange, NO_OPTIONS, readCompanySetups, UTILITY_SETUP_CHANGED } from 'src/stores/utilitySetups/companySetupsRead';
import { apPushNeeds, billToResidentsLabel, dayLabel, frequencyLabel, lastChangeLabel, pendingEditChanges, SETUP_TIPS, sourceLabel, sourceNeeds, usedByLabel } from './setupSummary';
import { IntakeAddress } from './IntakeAddress';
import { messageOf, Needs, sameId, Tip } from './parts';
import { PortalAccount } from './PortalAccount';
import { savedLine } from './setupEditor';
import useStyles from './styles';
import { UtilitySetupEditor } from './UtilitySetupEditor';

// Direct API calls (NO_OPTIONS), as the AP Push switch makes them: a store model's run() cancels its own in-flight call,
// and the setup page shows a section per utility.

// The picker's own choice, never a setup id.
const MAKE_OWN = '__make-own';

// A link to a portal setup refused because the utility has no portal login yet (LB-6184).
const PORTAL_LOGIN_MISSING = 'portal_login_missing';

// A company can have many setups of one type: the list scrolls under "Make its own setup", which stays at the top.
const MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320
    }
  },
  MenuListProps: {
    sx: {
      paddingTop: 0
    }
  }
};
const Row = _ref => {
  let label = _ref.label,
    tip = _ref.tip,
    children = _ref.children;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.row
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.label
  }, label, tip ? /*#__PURE__*/React.createElement(Tip, {
    label: label,
    text: tip
  }) : null), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    className: classes.value
  }, children));
};

/**
 * The setup a property utility follows (PRD V3, FR-2, FR-4, FR-10): pick another of the company's setups of its type,
 * make it its own setup, edit the setup (LB-6189), and see what the setup does and how many utilities follow it. Reads
 * nothing and renders nothing while isBillingOptionsEnabled is off. A utility that isn't found has no setup to follow;
 * one the caller may not see says so.
 *
 * `onLinkRead` hears every answer, so the page knows whether the utility follows a setup. `onLinked` hears the setup a
 * link, fork or setup edit applied at once, whose values the utility now has. `onSavingChange` hears when a link, fork,
 * cancelled move or setup edit starts and ends, and `disabled` holds every choice while the page saves.
 *
 * With `propertyId` and `provider` ({ utilityProviderId, name, isFetchable }), a setup on Utility portal shows this
 * utility's portal login under its source (LB-6191), and so does a link, or a setup edit, refused because utilities have
 * none yet, so the login can be saved right there. `onPortalLoginShown` hears whether that block is on screen, so a page that shows
 * the login elsewhere can leave its own out. `onLinkReadFailed` hears whether the last read failed or was refused: the
 * section then shows no login, so that page's own comes back.
 */
export const UtilitySetupSection = observer(_ref2 => {
  var _useCurrentSubscripti, _useCurrentSubscripti2, _link$setup, _link$setup2, _setup$id;
  let propertyUtilityId = _ref2.propertyUtilityId,
    utilityTypeId = _ref2.utilityTypeId,
    propertyId = _ref2.propertyId,
    provider = _ref2.provider,
    onLinkRead = _ref2.onLinkRead,
    onLinked = _ref2.onLinked,
    onSavingChange = _ref2.onSavingChange,
    onPortalLoginShown = _ref2.onPortalLoginShown,
    onLinkReadFailed = _ref2.onLinkReadFailed,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    className = _ref2.className;
  const classes = useStyles();
  const store = useStore();
  const enabled = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const isAppfolio = !!store.company.companyFeatureStore.isAppfolio;
  const allows = (_useCurrentSubscripti = (_useCurrentSubscripti2 = useCurrentSubscriptionTier()) === null || _useCurrentSubscripti2 === void 0 ? void 0 : _useCurrentSubscripti2.allows) !== null && _useCurrentSubscripti !== void 0 ? _useCurrentSubscripti : null;
  // Observed, so a company switch (impersonation, the dashboard picker) re-renders this and reads the new company's
  // setups. The company itself is localStorage's, which every switch writes first.
  const companyAccessId = store.homeDashboardStore.companyAccessId;
  const companyId = getCurrentCompanyId();

  // undefined while the first read runs; null when there's nothing to show.
  const _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    link = _useState2[0],
    setLink = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    readFailed = _useState4[0],
    setReadFailed = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    forbidden = _useState6[0],
    setForbidden = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    setups = _useState8[0],
    setSetups = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    setupsFailed = _useState0[0],
    setSetupsFailed = _useState0[1];
  const _useState1 = useState(0),
    _useState10 = _slicedToArray(_useState1, 2),
    setupsVersion = _useState10[0],
    setSetupsVersion = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    saving = _useState12[0],
    setSaving = _useState12[1];
  const _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    error = _useState14[0],
    setError = _useState14[1];
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    toLink = _useState16[0],
    setToLink = _useState16[1];
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    makingOwn = _useState18[0],
    setMakingOwn = _useState18[1];
  const _useState19 = useState(''),
    _useState20 = _slicedToArray(_useState19, 2),
    ownName = _useState20[0],
    setOwnName = _useState20[1];
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    editing = _useState22[0],
    setEditing = _useState22[1];
  const _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    editorSaving = _useState24[0],
    setEditorSaving = _useState24[1];
  const _useState25 = useState(''),
    _useState26 = _slicedToArray(_useState25, 2),
    notice = _useState26[0],
    setNotice = _useState26[1];
  // 'link' | 'edit' | false: a link, or an edit of the setup, was refused for want of portal logins. The login opens
  // until the next write here.
  const _useState27 = useState(false),
    _useState28 = _slicedToArray(_useState27, 2),
    loginNeeded = _useState28[0],
    setLoginNeeded = _useState28[1];

  // Reads can overlap -- a retry, a write here or beside it -- and only the latest may answer.
  const latestRead = useRef(0);
  // The setup an edit applied at once to, until a read hands its values on.
  const handOff = useRef(null);
  const listeners = useRef({
    onLinkRead,
    onLinked,
    onSavingChange,
    onPortalLoginShown,
    onLinkReadFailed
  });
  listeners.current = {
    onLinkRead,
    onLinked,
    onSavingChange,
    onPortalLoginShown,
    onLinkReadFailed
  };
  const show = useCallback(answer => {
    var _listeners$current$on, _listeners$current, _listeners$current$on2, _listeners$current2;
    setLink(answer);
    (_listeners$current$on = (_listeners$current = listeners.current).onLinkReadFailed) === null || _listeners$current$on === void 0 || _listeners$current$on.call(_listeners$current, false);
    (_listeners$current$on2 = (_listeners$current2 = listeners.current).onLinkRead) === null || _listeners$current$on2 === void 0 || _listeners$current$on2.call(_listeners$current2, answer);
  }, []);

  // `appliedSetupId` names a setup whose edit applied at once: when this utility follows it, its form takes the values.
  // It's kept until a read succeeds, so a Retry after a failed one still hands them on.
  const load = useCallback(async appliedSetupId => {
    if (appliedSetupId) handOff.current = appliedSetupId;
    const read = ++latestRead.current;
    try {
      var _resp$response$setup, _listeners$current$on3, _listeners$current3;
      const resp = await api.UtilitySetups.getLink({
        propertyUtilityId
      }, NO_OPTIONS);
      if (read !== latestRead.current) return;
      // An answer with nothing in it is a failed read, not "follows no setup": that would open fields the setup may own.
      if (!(resp !== null && resp !== void 0 && resp.response)) throw new Error('No setup answer');
      show(resp.response);
      setReadFailed(false);
      setForbidden(false);
      const applied = handOff.current;
      handOff.current = null;
      if (applied && sameId((_resp$response$setup = resp.response.setup) === null || _resp$response$setup === void 0 ? void 0 : _resp$response$setup.id, applied)) (_listeners$current$on3 = (_listeners$current3 = listeners.current).onLinked) === null || _listeners$current$on3 === void 0 || _listeners$current$on3.call(_listeners$current3, resp.response.setup);
    } catch (err) {
      var _err$response, _listeners$current$on4, _listeners$current4, _listeners$current$on5, _listeners$current5;
      if (read !== latestRead.current) return;
      const status = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 ? void 0 : _err$response.status;
      setLink(null);
      setReadFailed(![403, 404].includes(status));
      setForbidden(status === 403);
      (_listeners$current$on4 = (_listeners$current4 = listeners.current).onLinkReadFailed) === null || _listeners$current$on4 === void 0 || _listeners$current$on4.call(_listeners$current4, status !== 404);
      // Not found means no setup to follow (the routes answer 404 while property-service has billing options off),
      // so the utility's own fields open. Forbidden keeps them held, and the section says why.
      if (status === 404) (_listeners$current$on5 = (_listeners$current5 = listeners.current).onLinkRead) === null || _listeners$current$on5 === void 0 || _listeners$current$on5.call(_listeners$current5, {
        setup: null,
        pendingLink: null
      });
    }
  }, [propertyUtilityId, show]);
  useEffect(() => {
    if (!enabled || !propertyUtilityId) return undefined;
    const reloadAll = event => {
      var _event$detail, _event$detail2;
      if ((event === null || event === void 0 || (_event$detail = event.detail) === null || _event$detail === void 0 ? void 0 : _event$detail.propertyUtilityId) !== propertyUtilityId) load(event === null || event === void 0 || (_event$detail2 = event.detail) === null || _event$detail2 === void 0 ? void 0 : _event$detail2.appliedSetupId);
      setSetupsVersion(version => version + 1);
    };
    // Another utility in this place: nothing of the last one's stays on screen, or a Link confirmed now would send
    // this utility's id with a setup picked for that one.
    setLink(undefined);
    setReadFailed(false);
    setForbidden(false);
    setError('');
    setToLink(null);
    setMakingOwn(false);
    setEditing(false);
    setNotice('');
    setLoginNeeded(false);
    handOff.current = null;
    load();
    window.addEventListener(UTILITY_SETUP_CHANGED, reloadAll);
    return () => window.removeEventListener(UTILITY_SETUP_CHANGED, reloadAll);
  }, [enabled, propertyUtilityId, load]);
  const typeId = (link === null || link === void 0 || (_link$setup = link.setup) === null || _link$setup === void 0 ? void 0 : _link$setup.utilityTypeId) || utilityTypeId;

  // The portal login shows under a setup on Utility portal, or under a link refused for want of one. Not while a read
  // failed or was refused: the section shows only that.
  const showsPortalLogin = enabled && !!propertyUtilityId && !!provider && !readFailed && !forbidden && ((link === null || link === void 0 || (_link$setup2 = link.setup) === null || _link$setup2 === void 0 ? void 0 : _link$setup2.billSource) === 'UtilityPortal' || !!loginNeeded);
  useEffect(() => {
    var _listeners$current$on6, _listeners$current6;
    (_listeners$current$on6 = (_listeners$current6 = listeners.current).onPortalLoginShown) === null || _listeners$current$on6 === void 0 || _listeners$current$on6.call(_listeners$current6, showsPortalLogin);
  }, [showsPortalLogin]);
  useEffect(() => {
    if (!enabled || !companyId || !typeId) return undefined;
    let current = true;
    readCompanySetups(companyId, typeId).then(list => {
      if (!current) return;
      setSetupsFailed(list === null);
      setSetups((list !== null && list !== void 0 ? list : []).filter(setup => !setup.isArchived && sameId(setup.utilityTypeId, typeId)));
    });
    return () => {
      current = false;
    };
  }, [enabled, companyId, companyAccessId, typeId, setupsVersion]);
  if (!enabled || !propertyUtilityId) return null;
  if (readFailed) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      className: className,
      action: /*#__PURE__*/React.createElement(Button, {
        size: "small",
        onClick: () => load()
      }, "Retry")
    }, "Couldn't load the setup.");
  }
  if (forbidden) {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light",
      component: "p",
      className: className
    }, "This utility's setup can't be shown to you, so its billing options can't be changed here.");
  }
  if (!link) return null;
  const setup = link.setup,
    pendingLink = link.pendingLink;
  // The setup it follows stays a choice even when the company's list couldn't be read.
  const choices = setup && !setups.some(choice => sameId(choice.id, setup.id)) ? [setup, ...setups] : setups;

  // `everySection`: a write whose answer isn't this utility's link (a setup's own change), so every section, this one
  // included, reads again.
  const write = async function (call, fallback) {
    var _listeners$current$on7, _listeners$current7;
    let _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      _ref3$everySection = _ref3.everySection,
      everySection = _ref3$everySection === void 0 ? false : _ref3$everySection;
    setSaving(true);
    setError('');
    setNotice('');
    setLoginNeeded(false);
    (_listeners$current$on7 = (_listeners$current7 = listeners.current).onSavingChange) === null || _listeners$current$on7 === void 0 || _listeners$current$on7.call(_listeners$current7, true);
    try {
      const resp = await call();
      const answer = resp === null || resp === void 0 ? void 0 : resp.response;
      if (everySection) {
        announceSetupChange(null);
        return answer !== null && answer !== void 0 ? answer : null;
      }
      announceSetupChange(propertyUtilityId);
      if (!answer) {
        // Saved, but the answer says nothing about the link now: read it rather than assume.
        load();
        return null;
      }
      latestRead.current += 1;
      show(answer);
      return answer;
    } catch (err) {
      var _err$response2;
      setError(messageOf(err, fallback));
      setLoginNeeded((err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.data) === null || _err$response2 === void 0 || (_err$response2 = _err$response2.Error) === null || _err$response2 === void 0 ? void 0 : _err$response2.ErrorCode) === PORTAL_LOGIN_MISSING ? 'link' : false);
      return null;
    } finally {
      var _listeners$current$on8, _listeners$current8;
      setSaving(false);
      (_listeners$current$on8 = (_listeners$current8 = listeners.current).onSavingChange) === null || _listeners$current$on8 === void 0 || _listeners$current$on8.call(_listeners$current8, false);
    }
  };
  const handleConfirmLink = async () => {
    var _listeners$current$on9, _listeners$current9;
    const chosen = toLink;
    setToLink(null);
    const answer = await write(() => api.UtilitySetups.link({
      propertyUtilityId,
      setupId: chosen.id
    }, NO_OPTIONS), 'Could not link the setup.');
    // Applied at once: the utility now has the setup's values. A scheduled move changes nothing yet.
    if (answer !== null && answer !== void 0 && answer.setup && sameId(answer.setup.id, chosen.id) && !answer.pendingLink) (_listeners$current$on9 = (_listeners$current9 = listeners.current).onLinked) === null || _listeners$current$on9 === void 0 || _listeners$current$on9.call(_listeners$current9, answer.setup);
  };
  const handleCloseMakeOwn = () => {
    setMakingOwn(false);
    setOwnName('');
  };
  const handleConfirmMakeOwn = async () => {
    var _listeners$current$on0, _listeners$current0;
    setMakingOwn(false);
    const name = ownName.trim();
    const answer = await write(() => api.UtilitySetups.fork({
      propertyUtilityId,
      name: name || null
    }, NO_OPTIONS), 'Could not make its own setup.');
    if (answer) setOwnName('');
    if (answer !== null && answer !== void 0 && answer.setup && !answer.pendingLink) (_listeners$current$on0 = (_listeners$current0 = listeners.current).onLinked) === null || _listeners$current$on0 === void 0 || _listeners$current$on0.call(_listeners$current0, answer.setup);
  };
  const handleCancelMove = () => write(() => api.UtilitySetups.cancelPendingLink({
    propertyUtilityId
  }, NO_OPTIONS), 'Could not cancel the move.');

  // Withdraws the setup's scheduled edit. It changes no utility, but every section on the setup shows it, so all read again.
  const handleCancelEdit = () => write(() => api.UtilitySetups.cancelPendingEdit({
    setupId: setup.id
  }, NO_OPTIONS), 'Could not cancel the edit.', {
    everySection: true
  });
  const handleEditorSaving = on => {
    var _listeners$current$on1, _listeners$current1;
    setEditorSaving(on);
    (_listeners$current$on1 = (_listeners$current1 = listeners.current).onSavingChange) === null || _listeners$current$on1 === void 0 || _listeners$current$on1.call(_listeners$current1, on);
  };
  const handleEdited = (result, _ref4) => {
    let appliedNow = _ref4.appliedNow;
    setEditing(false);
    setLoginNeeded(false);
    setNotice(savedLine(result, appliedNow));
  };

  // The editor's latest answer: a refusal for want of logins opens this utility's login, and any other answer closes
  // what that refusal opened. A link's refusal stays until the next write.
  const handleLoginMissing = missing => setLoginNeeded(current => missing ? 'edit' : current === 'edit' ? false : current);
  const handlePick = event => {
    const value = event.target.value;
    if (value === MAKE_OWN) {
      setMakingOwn(true);
      return;
    }
    const chosen = choices.find(choice => sameId(choice.id, value));
    if (chosen && !sameId(chosen.id, setup === null || setup === void 0 ? void 0 : setup.id)) setToLink(chosen);
  };

  // The page's own Save sends every utility's method and frequency, so nothing here moves while it runs.
  const busy = saving || editorSaving || disabled;
  const editable = !!setup && setup.hiddenProperties === 0 && !setup.isArchived;
  const sourceNeed = setup ? sourceNeeds(setup.billSource, allows) : null;
  const apPushNeed = apPushNeeds(allows);
  const lastChange = setup ? lastChangeLabel(setup) : null;
  const editChanges = setup ? pendingEditChanges(setup) : [];
  return /*#__PURE__*/React.createElement("div", {
    className: className ? "".concat(classes.section, " ").concat(className) : classes.section,
    "data-testid": "utility-setup-section"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    component: "div",
    className: classes.heading
  }, "Setup", /*#__PURE__*/React.createElement(Tip, {
    label: "Setup",
    text: SETUP_TIPS.setup
  })), !setup && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light"
  }, "This utility doesn't follow a setup yet."), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "linked-setup-".concat(propertyUtilityId),
    size: "small",
    label: "Linked setup",
    className: classes.picker,
    value: (_setup$id = setup === null || setup === void 0 ? void 0 : setup.id) !== null && _setup$id !== void 0 ? _setup$id : '',
    onChange: handlePick,
    disabled: busy,
    InputLabelProps: {
      shrink: true
    },
    SelectProps: {
      displayEmpty: true,
      renderValue: value => {
        var _choices$find$name, _choices$find;
        return (_choices$find$name = (_choices$find = choices.find(choice => sameId(choice.id, value))) === null || _choices$find === void 0 ? void 0 : _choices$find.name) !== null && _choices$find$name !== void 0 ? _choices$find$name : 'Choose a setup';
      },
      MenuProps: MENU_PROPS
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: MAKE_OWN,
    className: classes.makeOwn
  }, setup ? 'Make its own setup from this one' : 'Make its own setup'), choices.map(choice => /*#__PURE__*/React.createElement(MenuItem, {
    key: choice.id,
    value: choice.id
  }, choice.name))), setupsFailed && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setSetupsVersion(version => version + 1)
    }, "Retry")
  }, "Couldn't load your company's other setups."), pendingLink && /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    action: /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: handleCancelMove,
      disabled: busy
    }, "Cancel move")
  }, "Moves to ", pendingLink.setupName, " from ", dayLabel(pendingLink.effectiveFrom), "."), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    onClose: () => setError('')
  }, error), showsPortalLogin && loginNeeded && (setup === null || setup === void 0 ? void 0 : setup.billSource) !== 'UtilityPortal' && /*#__PURE__*/React.createElement(React.Fragment, null, loginNeeded === 'edit' && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "The setup can't move to Utility portal until every utility that follows it has a portal login."), /*#__PURE__*/React.createElement(PortalAccount, {
    propertyId: propertyId,
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: typeId,
    provider: provider
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, loginNeeded === 'edit' ? "Save this utility's login here, and the others' in their own Setup sections. Then edit the setup again." : 'Then link it again.')), notice && /*#__PURE__*/React.createElement(Alert, {
    severity: "success",
    onClose: () => setNotice('')
  }, notice), setup && /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement(Row, {
    label: "How bills reach Livable",
    tip: SETUP_TIPS.source
  }, sourceLabel(setup.billSource), sourceNeed ? /*#__PURE__*/React.createElement(Needs, {
    tier: sourceNeed,
    className: classes.needs
  }) : null), setup.billSource === 'Email' && /*#__PURE__*/React.createElement(IntakeAddress, null), showsPortalLogin && setup.billSource === 'UtilityPortal' && /*#__PURE__*/React.createElement(PortalAccount, {
    propertyId: propertyId,
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: typeId,
    provider: provider
  }), /*#__PURE__*/React.createElement(Row, {
    label: "Bill to residents",
    tip: SETUP_TIPS.billToResidents
  }, billToResidentsLabel(setup)), /*#__PURE__*/React.createElement(Row, {
    label: "Bill frequency",
    tip: SETUP_TIPS.frequency
  }, frequencyLabel(setup.billFrequencyType)), isAppfolio && /*#__PURE__*/React.createElement(Row, {
    label: "AP Push",
    tip: SETUP_TIPS.apPush
  }, setup.isApPushEnabled ? 'On' : 'Off', apPushNeed ? /*#__PURE__*/React.createElement(Needs, {
    tier: apPushNeed,
    className: classes.needs
  }) : null), /*#__PURE__*/React.createElement(Row, {
    label: "Used by"
  }, usedByLabel(setup)), lastChange && /*#__PURE__*/React.createElement(Row, {
    label: "Last change"
  }, lastChange), setup.pendingEdit && /*#__PURE__*/React.createElement("div", {
    className: classes.pending
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "An edit to this setup applies from ", dayLabel(setup.pendingEdit.effectiveFrom), setup.pendingEdit.changedByName ? ", scheduled by ".concat(setup.pendingEdit.changedByName) : '', editChanges.length ? ": ".concat(editChanges.join(', '), ".") : '.'), editable && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    onClick: handleCancelEdit,
    disabled: busy
  }, "Cancel edit")), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, setup.hiddenProperties > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, "You can't edit this setup: it also drives ", setup.hiddenProperties, ' ', setup.hiddenProperties === 1 ? 'property' : 'properties', " you can't see."), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "text",
    onClick: () => setMakingOwn(true),
    disabled: busy
  }, "Make a new setup from it")) : /*#__PURE__*/React.createElement(React.Fragment, null, editable && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => setEditing(true),
    disabled: busy
  }, "Edit setup"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => setMakingOwn(true),
    disabled: busy
  }, "Make its own setup")))), editing && setup && /*#__PURE__*/React.createElement(UtilitySetupEditor, {
    setup: setup,
    open: true,
    onClose: () => setEditing(false),
    onSaved: handleEdited,
    onSavingChange: handleEditorSaving,
    onLoginsMissing: handleLoginMissing
  }), !setup && /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => setMakingOwn(true),
    disabled: busy
  }, "Make its own setup")), /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!toLink,
    handleClose: () => setToLink(null),
    title: toLink ? "Link to ".concat(toLink.name, "?") : '',
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: () => setToLink(null),
      variant: "text"
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleConfirmLink,
      size: "small",
      variant: "contained"
    }, "Link")
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "This utility takes the setup's bill source, Bill to residents and allocation method, bill frequency and AP Push. If it has been billed and anything changes, the move waits until the month in progress closes.")), /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: makingOwn,
    handleClose: handleCloseMakeOwn,
    title: "Make its own setup?",
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleCloseMakeOwn,
      variant: "text"
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleConfirmMakeOwn,
      size: "small",
      variant: "contained"
    }, "Make setup")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dialogBody
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "A new setup is made with this utility's current settings, and this utility follows it. Nothing it bills changes."), /*#__PURE__*/React.createElement(TextField, {
    id: "own-setup-name-".concat(propertyUtilityId),
    size: "small",
    label: "Setup name (optional)",
    value: ownName,
    onChange: event => setOwnName(event.target.value),
    inputProps: {
      maxLength: 200
    },
    helperText: "Left blank, it's named after its type, like Water 2."
  }))));
});